/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApiService } from 'app/api';
import { IApplicationContext } from 'app/context/types';
import { SearchCardsFieldsForm } from '../components/Card/CardsSearchBar';
import {
	ICategory,
	ICategoryListItem,
	ICreateCategory,
} from '../interfaces/category';
import { ICard, ICreateCard } from '../interfaces/cultureCard';
import { ICreateGuide, IGuide, IGuideListItem } from '../interfaces/guide';

export type ICardGetManySearchQuery = SearchCardsFieldsForm & {
	page: number;
};

export class CultureCardService {
	private apiService: ApiService;

	constructor(private applicationContext: IApplicationContext) {
		this.apiService = new ApiService(this.applicationContext);
	}

	getGuides(page: number, searchKey: string, id?: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().CULTURE_CARD.GET_GUIDES,
					null,
					{
						pathParams: {
							page,
							...(searchKey ? { searchKey } : {}),
							...(id ? { id } : {}),
						},
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	createGuide(newGuide: IGuide) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().CULTURE_CARD.CREATE_GUIDE,
					this.formatGuideData(newGuide),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	updateGuide(guide: IGuide): Promise<IGuideListItem> {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().CULTURE_CARD.UPDATE_GUIDE,
					this.formatGuideData(guide),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	deleteGuide(guideId: number) {
		return this.apiService.invoke(
			this.applicationContext.getApiConfig().CULTURE_CARD.DELETE_GUIDE,
			null,
			{ reqPathParam: guideId },
		);
	}

	getCategories(page: number, searchKey: string, id?: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().CULTURE_CARD.GET_CATEGORIES,
					null,
					{
						pathParams: {
							page,
							...(searchKey ? { searchKey } : {}),
							...(id ? { id } : {}),
						},
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	createCategory(newCategory: ICategory) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().CULTURE_CARD.CREATE_CATEGORY,
					this.formatCategoryData(newCategory),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	updateCategory(guide: ICategory): Promise<ICategoryListItem> {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().CULTURE_CARD.UPDATE_CATEGORY,
					this.formatCategoryData(guide),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	deleteCategory(guideId: number) {
		return this.apiService.invoke(
			this.applicationContext.getApiConfig().CULTURE_CARD.DELETE_CATEGORY,
			null,
			{ reqPathParam: guideId },
		);
	}

	searchGuides(searchKey: string) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().CULTURE_CARD.SEARCH_GUIDES,
					null,
					{
						pathParams: {
							searchKey,
						},
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	searchCategories(searchKey: string) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().CULTURE_CARD.SEARCH_CATEGORIES,
					null,
					{
						pathParams: {
							searchKey,
						},
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	getCards(searchQuery: ICardGetManySearchQuery) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().CULTURE_CARD.GET_CARDS,
					null,
					{
						pathParams: this.formGetCardsSearchQuery(searchQuery),
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	searchCards(searchKey: string) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().CULTURE_CARD.SEARCH_CARDS,
					null,
					{
						pathParams: { searchKey },
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	deleteCard(cardId: number) {
		return this.apiService.invoke(
			this.applicationContext.getApiConfig().CULTURE_CARD.DELETE_CARD,
			null,
			{ reqPathParam: cardId },
		);
	}

	getCard(id: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().CULTURE_CARD.GET_CARD,
					{},
					{
						reqPathParam: id,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	createCard(newCard: ICard) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().CULTURE_CARD.CREATE_CARD,
					this.formatCardData(newCard),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	updateCard(updatedCard: ICard) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().CULTURE_CARD.UPDATE_CARD,
					this.formatCardData(updatedCard),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	private formatCardData(newCard: ICard): ICreateCard {
		return {
			...(newCard.id ? { id: newCard.id } : {}),
			title: newCard.title,
			...(newCard.subtitle ? { subtitle: newCard.subtitle } : {}),
			categoryId: newCard.category.id,
			imageId: newCard.image.id,
			...(newCard.lesson ? { lessonId: newCard.lesson.id } : {}),
			slides: newCard.slides.map(slide => ({
				...(slide.id ? { id: slide.id } : {}),
				backgroundId: slide.background.id,
				...(slide.guide
					? { guideId: slide.guide.id, description: slide.description }
					: {}),
			})),
		};
	}

	private formGetCardsSearchQuery(searchQuery: ICardGetManySearchQuery) {
		const formatedSearchQuery: any = { ...searchQuery };
		if (searchQuery.lessonsRegion !== -1) {
			formatedSearchQuery.lessonsRegion = searchQuery.lessonsRegion;
		} else {
			delete formatedSearchQuery.lessonsRegion;
		}

		return formatedSearchQuery;
	}

	private formatGuideData(guide: IGuide): ICreateGuide {
		return {
			...(guide.id ? { id: guide.id } : {}),
			name: guide.name,
			imageId: guide.image.id,
		};
	}

	private formatCategoryData(guide: ICategory): ICreateCategory {
		return {
			...(guide.id ? { id: guide.id } : {}),
			title: guide.title,
			iconId: guide.icon.id,
		};
	}
}
